import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  Grid,
  Chip,
  Badge,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Menu,
  MenuItem,
  InputAdornment,
  Drawer,
  Paper,
  ListSubheader,
  ListItemButton,
  ListItemIcon,
  IconButton,
  Skeleton,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Search as SearchIcon,
  School,
  People,
  InfoOutlined,
  MoreVert,
  Close,
} from '@mui/icons-material';
import { Course, Post, Comment as CommentType } from '../../types';
import { initials } from '../../utils/strings';
import { apiListCourses, apiJoinCourse, apiLeaveCourse, apiListCoursePosts, apiCreateCoursePost, apiListMyCourses, type CourseListItemDto, apiListCourseMembers, type CourseMember, type ListCourseMembersResp, apiAddComment, apiDeleteComment, apiDeletePost } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

const SUPER_ADMIN_EMAIL = 'hl865@drexel.edu';

// Gradient-initial avatar treatment, matching the Home feed look (ember spark).
const AVATAR_GRAD = 'linear-gradient(140deg,#FFC24B 0%,#FF7A36 60%,#FF5B2E 100%)';
const gradAvatarSx = { background: AVATAR_GRAD, color: '#fff', fontWeight: 700 } as const;

// Courses will be loaded from API
const mockCourses: Course[] = [];

const mockCoursePosts: Post[] = [
  {
    id: '1',
    authorId: '2',
    author: {
      id: '2',
      email: 'sarah.johnson@drexel.edu',
      firstName: 'Sarah',
      lastName: 'Johnson',
      displayName: 'Sarah Johnson',
      major: 'Computer Science',
      year: 2,
      hometown: 'New York, NY',
      interests: ['Programming', 'AI', 'Music'],
      clubs: ['Drexel Gaming Association'],
      courses: [],
      createdAt: new Date(),
      updatedAt: new Date(),
    },
    content: 'Does anyone have the solution to problem 3 in the homework? I\'m stuck on the recursion part.',
    likes: ['1'],
    comments: [],
    courseId: '1',
    tags: ['Homework', 'Help'],
    createdAt: new Date(Date.now() - 1800000), // 30 minutes ago
    updatedAt: new Date(Date.now() - 1800000),
  },
  {
    id: '2',
    authorId: '3',
    author: {
      id: '3',
      email: 'mike.chen@drexel.edu',
      firstName: 'Mike',
      lastName: 'Chen',
      displayName: 'Mike Chen',
      major: 'Engineering',
      year: 4,
      hometown: 'Philadelphia, PA',
      interests: ['Engineering', 'Robotics', 'Sports'],
      clubs: ['Drexel Robotics Club'],
      courses: [],
      createdAt: new Date(),
      updatedAt: new Date(),
    },
    content: 'Great study session today! Thanks everyone for the help with the algorithms.',
    likes: ['1', '2'],
    comments: [],
    courseId: '1',
    tags: ['Study Group'],
    createdAt: new Date(Date.now() - 3600000), // 1 hour ago
    updatedAt: new Date(Date.now() - 3600000),
  },
];

const CourseChannels: React.FC = () => {
  function useDebounce<T>(value: T, delay = 300) {
    const [v, setV] = useState(value as T);
    useEffect(() => {
      const id = setTimeout(() => setV(value), delay);
      return () => clearTimeout(id);
    }, [value, delay]);
    return v as T;
  }
  const navigate = useNavigate();
  const location = useLocation();
  const { user: me } = useAuth();
  const isSuperAdmin = String(me?.email || '').toLowerCase() === SUPER_ADMIN_EMAIL;
  const theme = useTheme();
  const isMobileDialog = useMediaQuery(theme.breakpoints.down('sm'));
  const [query, setQuery] = useState('');
  const qDeb = useDebounce(query, 300);
  const [selectedCourse, setSelectedCourse] = useState<Course | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [newPostContent, setNewPostContent] = useState('');
  const [courses, setCourses] = useState<Course[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [pageWindowStart, setPageWindowStart] = useState(1);
  const pageSize = 10;
  const pageWindowSize = 10;
  const [pendingCourseId, setPendingCourseId] = useState<string | null>(null);
  const [posts, setPosts] = useState<Post[]>([]);
  const [deletingPostId, setDeletingPostId] = useState<string | null>(null);
  const [deletingCommentId, setDeletingCommentId] = useState<string | null>(null);
  const [confirmRemoveTarget, setConfirmRemoveTarget] = useState<{ postId: string; commentId?: string } | null>(null);
  const [postsCursor, setPostsCursor] = useState<string | null>(null);
  const [postsLoading, setPostsLoading] = useState(false);
  const [replyTarget, setReplyTarget] = useState<{ postId: string; parentId?: string; label: string } | null>(null);
  // My channels state
  const [myCourses, setMyCourses] = useState<Course[]>([]);
  const [myLoading, setMyLoading] = useState<boolean>(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  // Members dialog state
  const [membersOpen, setMembersOpen] = useState(false);
  const [mobileInfoOpen, setMobileInfoOpen] = useState(false);
  const [channelMenuAnchorEl, setChannelMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [members, setMembers] = useState<CourseMember[]>([]);
  const [membersCursor, setMembersCursor] = useState<string | null>(null);
  const [membersLoading, setMembersLoading] = useState(false);
  const [membersQuery, setMembersQuery] = useState('');
  const [unseenCourseIds, setUnseenCourseIds] = useState<string[]>([]);
  const membersQDeb = useDebounce(membersQuery, 300);
  const membersAcRef = useRef<AbortController | null>(null);
  const messagesContainerRef = useRef<HTMLDivElement | null>(null);
  const composerInputRef = useRef<HTMLInputElement | null>(null);
  const shouldAutoScrollToBottomRef = useRef(false);
  const openedFromStateRef = useRef<string | null>(null);
  const courseSeenMapKey = useMemo(() => (me?.id ? `dragonsframe_seen_course_channels_${me.id}` : null), [me?.id]);
  const unseenCourseIdsKey = useMemo(() => (me?.id ? `dragonsframe_unseen_course_ids_${me.id}` : null), [me?.id]);

  const syncUnseenCourseIds = () => {
    if (!unseenCourseIdsKey) {
      setUnseenCourseIds([]);
      return;
    }
    try {
      const raw = localStorage.getItem(unseenCourseIdsKey);
      const parsed = raw ? (JSON.parse(raw) as unknown) : [];
      setUnseenCourseIds(Array.isArray(parsed) ? parsed.filter((id): id is string => typeof id === 'string') : []);
    } catch {
      setUnseenCourseIds([]);
    }
  };

  const markCourseSeen = (courseId: string) => {
    if (!courseSeenMapKey || !unseenCourseIdsKey || !courseId) return;
    try {
      const rawSeen = localStorage.getItem(courseSeenMapKey);
      const seen = rawSeen ? (JSON.parse(rawSeen) as Record<string, unknown>) : {};
      seen[courseId] = Date.now();
      localStorage.setItem(courseSeenMapKey, JSON.stringify(seen));
    } catch {
      localStorage.setItem(courseSeenMapKey, JSON.stringify({ [courseId]: Date.now() }));
    }

    try {
      const rawUnseen = localStorage.getItem(unseenCourseIdsKey);
      const parsed = rawUnseen ? (JSON.parse(rawUnseen) as unknown) : [];
      const current = Array.isArray(parsed) ? parsed.filter((id): id is string => typeof id === 'string') : [];
      const next = current.filter((id) => id !== courseId);
      localStorage.setItem(unseenCourseIdsKey, JSON.stringify(next));
      setUnseenCourseIds(next);
    } catch {
      setUnseenCourseIds((prev) => {
        const next = prev.filter((id) => id !== courseId);
        localStorage.setItem(unseenCourseIdsKey, JSON.stringify(next));
        return next;
      });
    }
  };

  const isCourseUnread = (courseId?: string | null) => {
    if (!courseId) return false;
    return unseenCourseIds.includes(courseId);
  };

  const isChannelMenuOpen = Boolean(channelMenuAnchorEl);

  function closeCourseDialog() {
    setIsDialogOpen(false);
    setMobileInfoOpen(false);
    setChannelMenuAnchorEl(null);
  }

  function openChannelMenu(event: React.MouseEvent<HTMLElement>) {
    setChannelMenuAnchorEl(event.currentTarget);
  }

  function closeChannelMenu() {
    setChannelMenuAnchorEl(null);
  }

  function leaveChannelFromMenu() {
    if (selectedCourse?.joined) {
      void handleLeave(selectedCourse);
    }
    closeChannelMenu();
  }

  const goToProfile = (userId?: string | null) => {
    if (!userId) return;
    closeCourseDialog();
    setMembersOpen(false);
    const target = me?.id && userId === me.id ? '/profile' : `/profile/${userId}`;
    navigate(target);
  };

  useEffect(() => {
    setCurrentPage(1);
    setPageWindowStart(1);
  }, [qDeb]);

  useEffect(() => {
    setPageWindowStart((prev) => {
      const maxStart = Math.max(1, Math.floor((Math.max(1, totalPages) - 1) / pageWindowSize) * pageWindowSize + 1);
      return Math.min(prev, maxStart);
    });
  }, [totalPages]);

  // Debounced search + page load; gated on auth with abort of stale requests
  useEffect(() => {
    let alive = true;
    if (!me) { setCourses([]); setTotalPages(1); return; }
    const ac = new AbortController();
    (async () => {
      try {
        setLoading(true);
    const resp: any = await apiListCourses(qDeb || undefined, undefined, pageSize, ac.signal, currentPage);
        if (!alive) return;
  setCourses((resp?.items || []).map(shapeCourseFromDto));
  setTotalPages(Math.max(1, resp?.totalPages ?? 1));
        setError(null);
      } catch (e: any) {
        if (!alive || e?.name === 'AbortError') return;
        if (e?.code === 401) { setCourses([]); setTotalPages(1); return; }
        setError(e?.message || 'Failed to load courses');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; ac.abort(); };
  }, [me, qDeb, currentPage]);

  // Load my joined courses
  useEffect(() => {
    const ac = new AbortController();
    (async () => {
      try {
        setMyLoading(true);
        if (!me) { setMyCourses([]); return; }
        const resp: any = await apiListMyCourses(ac.signal);
        const items = (resp?.items || []).map(shapeCourseFromDto);
        setMyCourses(items);
      } catch (e: any) {
        if (e?.name !== 'AbortError') console.error('loadMyCourses failed', e);
      } finally {
        setMyLoading(false);
      }
    })();
    return () => ac.abort();
  }, [me]);

  useEffect(() => {
    const openCourseId = (location.state as any)?.openCourseId as string | undefined;
    const openCourseCode = (location.state as any)?.openCourseCode as string | undefined;
    if (!openCourseId) return;
    if (openedFromStateRef.current === openCourseId) return;
    if (myLoading) return;

    openedFromStateRef.current = openCourseId;

    const local = myCourses.find((course) => course.id === openCourseId) || courses.find((course) => course.id === openCourseId);
    if (local) {
      void handleCourseClick(local);
    } else if (openCourseCode) {
      // Course isn't in the loaded lists (e.g. a professor's course the viewer hasn't
      // joined) — fetch it by code, then open its channel dialog.
      (async () => {
        try {
          const resp: any = await apiListCourses(openCourseCode, undefined, 24);
          const items = ((resp?.items || []).map(shapeCourseFromDto)) as Course[];
          const norm = (s?: string) => (s || '').replace(/\s+/g, '').toUpperCase();
          const match = items.find((c) => c.id === openCourseId) || items.find((c) => norm(c.code) === norm(openCourseCode));
          if (match) void handleCourseClick(match);
        } catch (e) {
          console.error('open course from chip failed', e);
        }
      })();
    }
    navigate('/courses', { replace: true, state: null });
  }, [location.state, myLoading, myCourses, courses, navigate]);

  useEffect(() => {
    syncUnseenCourseIds();
    if (!unseenCourseIdsKey) return;

    const onFocus = () => syncUnseenCourseIds();
    const intervalId = window.setInterval(syncUnseenCourseIds, 15000);
    window.addEventListener('focus', onFocus);

    return () => {
      window.removeEventListener('focus', onFocus);
      window.clearInterval(intervalId);
    };
  }, [unseenCourseIdsKey]);

  const { matchedCourses, recommendedCourses } = useMemo(() => {
    const term = qDeb.trim();
    if (!term) {
      return { matchedCourses: courses, recommendedCourses: [] as Course[] };
    }

    const compact = term.replace(/\s+/g, '').toLowerCase();
    const alpha = (term.match(/^[A-Za-z]{2,5}/)?.[0] || '').toLowerCase();
    const digits = (term.match(/\d{1,4}/)?.[0] || '').toLowerCase();

    const scoreCourse = (course: Course) => {
      const code = String(course.code || '').toLowerCase();
      const codeCompact = code.replace(/\s+/g, '');
      const title = String(course.name || '').toLowerCase();
      const subject = String((course as any).subject ?? course.department ?? '').toLowerCase();
      const number = String((course as any).number ?? '').toLowerCase();

      if (codeCompact === compact) return 300;
      if (alpha && digits && subject.startsWith(alpha) && number.startsWith(digits)) return 260;
      if (codeCompact.startsWith(compact)) return 220;
      if (codeCompact.includes(compact)) return 180;
      if (title.includes(term.toLowerCase())) return 100;
      if (alpha && subject.startsWith(alpha)) return 35;
      return 0;
    };

    const ranked = courses
      .map((course) => ({
        course,
        score: scoreCourse(course),
      }))
      .sort((a, b) => {
        if (b.score !== a.score) return b.score - a.score;
        return (b.course.memberCount ?? 0) - (a.course.memberCount ?? 0);
      });

    const matched = ranked.filter((entry) => entry.score >= 100).map((entry) => entry.course);
    const fallbackMatched = matched.length ? matched : ranked.slice(0, Math.min(6, ranked.length)).map((entry) => entry.course);
    const matchedIds = new Set(fallbackMatched.map((course) => course.id));
    const recommended = ranked
      .filter((entry) => !matchedIds.has(entry.course.id))
      .map((entry) => entry.course)
      .slice(0, 8);

    return {
      matchedCourses: fallbackMatched,
      recommendedCourses: recommended,
    };
  }, [courses, qDeb]);

  // Track an AbortController to cancel stale post loads when switching courses
  const postsLoadAcRef = useRef<AbortController | null>(null);

  async function handleCourseClick(course: Course) {
    let ac: AbortController | null = null;
    try {
      markCourseSeen(course.id);
      // cancel any in-flight posts fetch
      postsLoadAcRef.current?.abort();
      ac = new AbortController();
      postsLoadAcRef.current = ac;

      // select + prime UI
      setSelectedCourse(course);
      setPosts([]);
      setPostsCursor(null);
      setReplyTarget(null);
      setNewPostContent('');
      setPostsLoading(true);
      shouldAutoScrollToBottomRef.current = true;
      setIsDialogOpen(true);

      // fetch first page
      const resp: any = await apiListCoursePosts(course.id, undefined, 25, ac.signal);
      const items = (resp?.items || []).map(shapeFromDto);
      setPosts(items);
      setPostsCursor(resp?.nextCursor ?? null);
    } catch (e: any) {
      if (e?.name === 'AbortError') return; // switched courses
      console.error('open course failed', e);
    } finally {
      // Avoid racing with a newer load
      if (postsLoadAcRef.current === ac) setPostsLoading(false);
    }
  }

  function shapeFromDto(p: any): Post {
    return {
      id: p.id,
      authorId: p.authorId,
      author: (p.author as any) || ({ id: p.authorId, displayName: 'Unknown Student', profileImage: null } as any),
      content: p.content,
      likes: Array.isArray(p.likes) ? p.likes : [],
      comments: Array.isArray(p.comments) ? p.comments.map((c: any) => ({
        id: c.id,
        content: c.content,
        parentId: c.parentId ?? null,
        createdAt: new Date(c.createdAt),
        author: c.author,
        children: (c.children || []).map((d: any) => ({
          id: d.id,
          content: d.content,
          parentId: d.parentId ?? null,
          createdAt: new Date(d.createdAt),
          author: d.author,
          children: [],
        }))
      })) : [],
      courseId: p.courseId || undefined,
      tags: [],
      createdAt: new Date(p.createdAt),
      updatedAt: new Date(p.updatedAt),
    };
  }

  function shapeCourseFromDto(c: CourseListItemDto | any): Course {
    return {
      id: c.id,
      code: c.code,
      name: c.title ?? c.name,
      department: c.subject ?? c.department,
      credits: c.credits ?? null,
      description: c.description ?? '',
      memberCount: (c as any).memberCount ?? 0,
      joined: !!(c as any).joined,
    } as Course;
  }

  async function handleJoin(course: Course) {
    // optimistic
    setPendingCourseId(course.id);
    setCourses(prev => prev.map(c => c.id === course.id ? { ...c, joined: true, memberCount: (c.memberCount ?? 0) + 1 } : c));
    setSelectedCourse(prev => prev && prev.id === course.id ? { ...prev, joined: true, memberCount: (prev.memberCount ?? 0) + 1 } : prev);
    try {
      const r: any = await apiJoinCourse(course.id);
      setCourses(prev => prev.map(c => c.id === course.id ? { ...c, joined: !!r.joined, memberCount: r.memberCount ?? c.memberCount } : c));
      setSelectedCourse(prev => prev && prev.id === course.id ? { ...prev, joined: !!r.joined, memberCount: r.memberCount ?? prev.memberCount } : prev);
  // add to sidebar list if not present
  setMyCourses(prev => prev.some(mc => mc.id === course.id) ? prev : [{ ...course, joined: true }, ...prev]);
    } catch (e) {
      // rollback
      setCourses(prev => prev.map(c => c.id === course.id ? { ...c, joined: false, memberCount: Math.max(0, (c.memberCount ?? 1) - 1) } : c));
      setSelectedCourse(prev => prev && prev.id === course.id ? { ...prev, joined: false, memberCount: Math.max(0, (prev.memberCount ?? 1) - 1) } : prev);
    } finally { setPendingCourseId(null); }
  }

  async function handleLeave(course: Course) {
    // optimistic
    setPendingCourseId(course.id);
    setCourses(prev => prev.map(c => c.id === course.id ? { ...c, joined: false, memberCount: Math.max(0, (c.memberCount ?? 1) - 1) } : c));
    setSelectedCourse(prev => prev && prev.id === course.id ? { ...prev, joined: false, memberCount: Math.max(0, (prev.memberCount ?? 1) - 1) } : prev);
    try {
      const r: any = await apiLeaveCourse(course.id);
      setCourses(prev => prev.map(c => c.id === course.id ? { ...c, joined: !!r.joined, memberCount: r.memberCount ?? c.memberCount } : c));
      setSelectedCourse(prev => prev && prev.id === course.id ? { ...prev, joined: !!r.joined, memberCount: r.memberCount ?? prev.memberCount } : prev);
  // remove from sidebar list
  setMyCourses(prev => prev.filter(mc => mc.id !== course.id));
    } catch (e) {
      // rollback back to joined
      setCourses(prev => prev.map(c => c.id === course.id ? { ...c, joined: true, memberCount: (c.memberCount ?? 0) + 1 } : c));
      setSelectedCourse(prev => prev && prev.id === course.id ? { ...prev, joined: true, memberCount: (prev.memberCount ?? 0) + 1 } : prev);
    } finally { setPendingCourseId(null); }
  }

  function shapeCommentFromDto(c: any): CommentType {
    return {
      id: c.id,
      content: c.content,
      parentId: c.parentId ?? null,
      createdAt: new Date(c.createdAt),
      author: c.author,
      children: (c.children || []).map(shapeCommentFromDto),
    };
  }

  function appendReplyToComments(comments: CommentType[], parentId: string, child: CommentType): CommentType[] {
    return comments.map((comment) => {
      if (comment.id === parentId) {
        return { ...comment, children: [...(comment.children || []), child] };
      }
      if (comment.children?.length) {
        return { ...comment, children: appendReplyToComments(comment.children, parentId, child) };
      }
      return comment;
    });
  }

  const orderedPosts = useMemo(
    () => [...(posts || [])].sort((a, b) => a.createdAt.getTime() - b.createdAt.getTime()),
    [posts]
  );

  useEffect(() => {
    if (!isDialogOpen || postsLoading || !shouldAutoScrollToBottomRef.current) return;
    const container = messagesContainerRef.current;
    if (!container) return;
    requestAnimationFrame(() => {
      container.scrollTop = container.scrollHeight;
      shouldAutoScrollToBottomRef.current = false;
    });
  }, [isDialogOpen, postsLoading, orderedPosts.length]);

  useEffect(() => {
    if (!isDialogOpen || !replyTarget) return;
    requestAnimationFrame(() => {
      composerInputRef.current?.focus();
      const length = composerInputRef.current?.value.length ?? 0;
      composerInputRef.current?.setSelectionRange(length, length);
    });
  }, [isDialogOpen, replyTarget]);

  const handleDeletePost = async (postId: string) => {
    if (!postId || deletingPostId) return;
    const previousPosts = posts;
    setDeletingPostId(postId);
    setPosts((prev) => prev.filter((p) => p.id !== postId));
    try {
      await apiDeletePost(postId);
    } catch (e) {
      setPosts(previousPosts);
      console.error('delete post failed', e);
    } finally {
      setDeletingPostId(null);
      setConfirmRemoveTarget(null);
    }
  };

  function removeCommentFromTree(node: CommentType, targetId: string): CommentType | null {
    if (node.id === targetId) return null;
    return {
      ...node,
      children: (node.children || [])
        .map((child) => removeCommentFromTree(child, targetId))
        .filter(Boolean) as CommentType[],
    };
  }

  function softDeleteCommentInTree(node: CommentType, targetId: string): CommentType {
    if (node.id === targetId) {
      return {
        ...node,
        content: null,
        author: { id: '', displayName: '[deleted]', profileImage: null } as any,
      };
    }
    return {
      ...node,
      children: (node.children || []).map((child) => softDeleteCommentInTree(child, targetId)),
    };
  }

  const handleDeleteComment = async (postId: string, commentId: string) => {
    if (!postId || !commentId || deletingCommentId) return;
    const previousPosts = posts;
    setDeletingCommentId(commentId);
    // Show immediate feedback while preserving thread shape until server confirms hard delete.
    setPosts((prev) => prev.map((post) => {
      if (post.id !== postId) return post;
      const nextComments = (post.comments || []).map((comment) => softDeleteCommentInTree(comment, commentId));
      return { ...post, comments: nextComments };
    }));
    try {
      const resp = await apiDeleteComment(commentId);
      if (!(resp as any)?.softDeleted) {
        setPosts((prev) => prev.map((post) => {
          if (post.id !== postId) return post;
          const nextComments = (post.comments || [])
            .map((comment) => removeCommentFromTree(comment, commentId))
            .filter(Boolean) as CommentType[];
          return { ...post, comments: nextComments };
        }));
      }
    } catch (e) {
      setPosts(previousPosts);
      console.error('delete comment failed', e);
    } finally {
      setDeletingCommentId(null);
      setConfirmRemoveTarget(null);
    }
  };

  const handleCreatePost = async () => {
    if (!newPostContent.trim() || !selectedCourse) return;
    shouldAutoScrollToBottomRef.current = true;
    const draft = newPostContent.trim();
    const activeReply = replyTarget;

    if (activeReply) {
      setNewPostContent('');
      setReplyTarget(null);
      try {
        const saved: any = await apiAddComment(activeReply.postId, { content: draft, parentId: activeReply.parentId });
        const shaped = shapeCommentFromDto(saved);
        setPosts((prev) => prev.map((p) => {
          if (p.id !== activeReply.postId) return p;
          if (!activeReply.parentId) {
            return { ...p, comments: [...(p.comments || []), shaped] };
          }
          return { ...p, comments: appendReplyToComments(p.comments || [], activeReply.parentId, shaped) };
        }));
      } catch {
        setNewPostContent(draft);
        setReplyTarget(activeReply);
      }
      return;
    }

    const optimistic: Post = {
      id: `tmp-${Date.now()}`,
      authorId: '',
      author: ({ id: '', displayName: 'You', profileImage: null } as any),
      content: draft,
      likes: [],
      comments: [],
      courseId: selectedCourse.id,
      tags: [],
      createdAt: new Date(),
      updatedAt: new Date(),
    };
    setPosts(prev => [...prev, optimistic]);
    const toCreate = draft;
    setNewPostContent('');
    try {
      const created: any = await apiCreateCoursePost(selectedCourse.id, toCreate);
      setPosts(prev => [...prev.filter(p => p.id !== optimistic.id), shapeFromDto(created)]);
    } catch {
      setPosts(prev => prev.filter(p => p.id !== optimistic.id));
      setNewPostContent(toCreate);
    }
  };

  const formatTimeAgo = (date: Date) => {
    const now = new Date();
    const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / 60000);
    
    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `${diffInHours}h ago`;
    
    const diffInDays = Math.floor(diffInHours / 24);
    return `${diffInDays}d ago`;
  };

  const formatMessageTime = (date: Date) => {
    const diffMs = Math.max(0, Date.now() - date.getTime());
    const minutes = Math.max(1, Math.floor(diffMs / 60000));
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h`;
    const days = Math.floor(hours / 24);
    if (days < 7) return `${days}d`;
    const now = new Date();
    return date.toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      ...(date.getFullYear() !== now.getFullYear() ? { year: 'numeric' } : {}),
    });
  };

  const isCurrentUser = (authorId?: string | null) => {
    if (!me?.id || !authorId) return false;
    return authorId === me.id;
  };

  const getAuthorLabel = (author: any, fallbackAuthorId?: string | null) => {
    const resolvedAuthorId = author?.id || fallbackAuthorId || null;
    if (isCurrentUser(resolvedAuthorId)) return 'You';

    const byName = `${author?.firstName || ''} ${author?.lastName || ''}`.trim();
    return author?.displayName || byName || 'Student';
  };

  const isSameDay = (a: Date, b: Date) => (
    a.getFullYear() === b.getFullYear()
    && a.getMonth() === b.getMonth()
    && a.getDate() === b.getDate()
  );

  const formatDaySeparator = (date: Date) => {
    const now = new Date();
    const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const dateStart = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const diffDays = Math.floor((todayStart.getTime() - dateStart.getTime()) / 86400000);
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    return new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric', year: 'numeric' }).format(date);
  };

  async function openMembersDialog(course: Course) {
    setMembersOpen(true);
    setMembers([]); setMembersCursor(null); setMembersLoading(true);
    const ac = new AbortController(); membersAcRef.current = ac;
    try {
      const resp = await apiListCourseMembers(course.id, membersQDeb || undefined, undefined, 25, ac.signal);
      setMembers(resp.items || []); setMembersCursor(resp.nextCursor || null);
    } catch (e) { if ((e as any)?.name !== 'AbortError') console.error('load members failed', e); }
    finally { if (membersAcRef.current === ac) setMembersLoading(false); }
  }

  useEffect(() => {
    if (!membersOpen || !selectedCourse) return; // only refetch when dialog open
    const ac = new AbortController(); membersAcRef.current = ac;
    (async () => {
      setMembersLoading(true);
      try { const r = await apiListCourseMembers(selectedCourse.id, membersQDeb || undefined, undefined, 25, ac.signal); setMembers(r.items || []); setMembersCursor(r.nextCursor || null); }
      catch (e) { if ((e as any)?.name !== 'AbortError') console.error('members refresh failed', e); }
      finally { if (membersAcRef.current === ac) setMembersLoading(false); }
    })();
    return () => ac.abort();
  }, [membersQDeb, membersOpen, selectedCourse]);

  const showCourseGridSkeleton = loading && matchedCourses.length === 0;

  const MyChannelsSkeleton = () => (
    <>
      {Array.from({ length: 3 }).map((_, index) => (
        <ListItem key={`my-channels-skeleton-${index}`}>
          <ListItemIcon>
            <Skeleton variant="circular" width={32} height={32} animation="wave" />
          </ListItemIcon>
          <ListItemText
            primary={<Skeleton variant="text" width="85%" height={20} animation="wave" />}
            secondary={<Skeleton variant="text" width="55%" height={18} animation="wave" />}
            primaryTypographyProps={{ component: 'div' }}
            secondaryTypographyProps={{ component: 'div' }}
          />
        </ListItem>
      ))}
    </>
  );

  return (
    <Box sx={{ width: '100%', maxWidth: 1100, mx: 'auto' }}>
      <Typography variant="h4" gutterBottom component="div" sx={{ fontWeight: 800, letterSpacing: '-0.02em' }}>
        Course Channels
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }} component="div">
        Join course-specific discussion groups to connect with classmates and get help with assignments.
      </Typography>

      {/* Responsive grid: sidebar + content */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '280px 1fr' }, gap: 2 }}>
        {/* Sidebar (desktop) */}
        <Box sx={{ display: { xs: 'none', md: 'block' } }}>
          <Paper variant="outlined" sx={{ position: 'sticky', top: 16, maxHeight: 'calc(100vh - 32px)', overflow: 'auto' }}>
            <List
              subheader={<ListSubheader component="div" sx={{ bgcolor: 'background.paper' }}>My Channels</ListSubheader>}
            >
              {myLoading ? (
                <MyChannelsSkeleton />
              ) : myCourses.length === 0 ? (
                <ListItem>
                  <ListItemText
                    primary="No channels yet"
                    secondary="Join a course to pin it here"
                    primaryTypographyProps={{ component: 'div' }}
                    secondaryTypographyProps={{ component: 'div' }}
                  />
                </ListItem>
              ) : (
                myCourses.map((c) => (
                  <ListItemButton key={c.id} onClick={() => handleCourseClick(c)}>
                    <ListItemIcon>
                      <Badge
                        color="error"
                        variant="dot"
                        overlap="circular"
                        invisible={!isCourseUnread(c.id)}
                        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                      >
                        <Avatar sx={{ width: 32, height: 32, fontSize: 13, ...gradAvatarSx }}>{initials(c.code || c.name)}</Avatar>
                      </Badge>
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                          <Typography variant="body2" component="span" sx={{ fontWeight: 500 }}>{`${c.code} — ${c.name}`}</Typography>
                          {isCourseUnread(c.id) && <Chip size="small" color="error" label="New" />}
                        </Box>
                      }
                      secondary={`${(c as any).subject ?? c.department}${(c as any).number ? ` ${(c as any).number}` : ''}`}
                      primaryTypographyProps={{ component: 'div' }}
                      secondaryTypographyProps={{ noWrap: true, component: 'div' }}
                    />
                  </ListItemButton>
                ))
              )}
            </List>
          </Paper>
        </Box>

        {/* Main content (right) */}
        <Box>
          {/* Mobile toggle */}
          <Box sx={{ display: { xs: 'flex', md: 'none' }, mb: 1, justifyContent: 'flex-start' }}>
            <Button variant="outlined" onClick={() => setSidebarOpen(true)}>My Channels</Button>
          </Box>

          {/* Search */}
          <TextField
            fullWidth
            placeholder="Search courses..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ mb: 3 }}
          />

          {/* Course Grid */}
          <Grid container spacing={3}>
            {showCourseGridSkeleton && Array.from({ length: 6 }).map((_, index) => (
              <Grid item xs={12} sm={6} md={4} key={`course-skeleton-${index}`}>
                <Card sx={{ height: '100%' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                      <Skeleton variant="circular" width={40} height={40} animation="wave" />
                      <Box sx={{ flexGrow: 1 }}>
                        <Skeleton variant="text" width="45%" height={28} animation="wave" />
                        <Skeleton variant="text" width="35%" height={20} animation="wave" />
                      </Box>
                    </Box>
                    <Skeleton variant="text" width="70%" height={26} animation="wave" sx={{ mb: 1 }} />
                    <Skeleton variant="rectangular" height={84} animation="wave" sx={{ mb: 2 }} />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Skeleton variant="rounded" width={82} height={24} animation="wave" />
                      <Skeleton variant="text" width={96} height={22} animation="wave" />
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                      <Skeleton variant="rounded" width={112} height={32} animation="wave" />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}

            {!showCourseGridSkeleton && matchedCourses.map((course) => (
              <Grid item xs={12} sm={6} md={4} key={course.id}>
                <Card sx={{ height: '100%', cursor: 'pointer' }} onClick={() => handleCourseClick(course)}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                      <Avatar sx={gradAvatarSx}>
                        <School />
                      </Avatar>
                      <Box>
                        <Typography variant="h6" sx={{ fontWeight: 800, color: 'primary.main' }}>
                          <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
                            {course.code}
                            {course.joined && isCourseUnread(course.id) && <Chip size="small" color="error" label="New" />}
                          </Box>
                        </Typography>
                        <Typography variant="body2" color="text.secondary" component="div">
                          {course.department}
                        </Typography>
                      </Box>
                    </Box>
                    {(() => {
                      const rawTitle = course.name || '';
                      const displayTitle = rawTitle.replace(/\s*\(?\d+(\.\d+)?(\s*-\s*\d+(\.\d+)?)?\s*Credits?(\s*Hours?)?\)?$/i, '');
                      return (
                        <Typography variant="subtitle1" gutterBottom component="div">{displayTitle}</Typography>
                      );
                    })()}
                    <Box
                      sx={{
                        mb: 2,
                        minHeight: 84,
                        maxHeight: 84,
                        overflowY: 'auto',
                        pr: 0.5,
                      }}
                    >
                      <Typography variant="body2" color="text.secondary" component="div">
                        {course.description || 'No description provided.'}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      {(() => {
                        const hasCredits = typeof course.credits === 'number' && !Number.isNaN(course.credits);
                        if (!hasCredits) return null;
                        const val = course.credits as number;
                        const label = Number.isInteger(val) ? `${val.toFixed(0)} credits` : `${val} credits`;
                        return <Chip label={label} size="small" sx={{ mr: 1 }} />;
                      })()}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <People fontSize="small" color="action" />
                        <Typography variant="body2" component="div">{course.memberCount ?? 0} members</Typography>
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                      {course.joined ? (
                        <>
                          <Button size="small" variant="outlined" onClick={(e) => { e.stopPropagation(); handleCourseClick(course); }}>Go to Channel</Button>
                          <Button size="small" sx={{ ml: 1 }} variant="text" onClick={(e) => { e.stopPropagation(); openMembersDialog(course); }}>Members</Button>
                        </>
                      ) : (
                        <Button size="small" variant="contained" disabled={pendingCourseId === course.id} onClick={(e) => { e.stopPropagation(); handleJoin(course); }}>Join Channel</Button>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          {!!qDeb.trim() && recommendedCourses.length > 0 && (
            <Box sx={{ mt: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 2 }}>
                <Divider sx={{ flex: 1 }} />
                <Typography variant="overline" color="text.secondary" component="div" sx={{ letterSpacing: 1 }}>
                  Recommended Channels
                </Typography>
                <Divider sx={{ flex: 1 }} />
              </Box>

              <Grid container spacing={3}>
                {recommendedCourses.map((course) => (
                  <Grid item xs={12} sm={6} md={4} key={`recommended-${course.id}`}>
                    <Card sx={{ height: '100%', cursor: 'pointer' }} onClick={() => handleCourseClick(course)}>
                      <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                          <Avatar sx={{ bgcolor: 'primary.main' }}>
                            <School />
                          </Avatar>
                          <Box>
                            <Typography variant="h6" sx={{ fontWeight: 800, color: 'primary.main' }}>{course.code}</Typography>
                            <Typography variant="body2" color="text.secondary" component="div">
                              {course.department}
                            </Typography>
                          </Box>
                        </Box>
                        <Typography variant="subtitle1" gutterBottom component="div">
                          {(course.name || '').replace(/\s*\(?\d+(\.\d+)?(\s*-\s*\d+(\.\d+)?)?\s*Credits?(\s*Hours?)?\)?$/i, '')}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" component="div" sx={{ mb: 2 }}>
                          {course.description || 'No description provided.'}
                        </Typography>
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                          {course.joined ? (
                            <Button size="small" variant="outlined" onClick={(e) => { e.stopPropagation(); handleCourseClick(course); }}>
                              Go to Channel
                            </Button>
                          ) : (
                            <Button size="small" variant="contained" disabled={pendingCourseId === course.id} onClick={(e) => { e.stopPropagation(); handleJoin(course); }}>
                              Join Channel
                            </Button>
                          )}
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}

          {!loading && matchedCourses.length > 0 && totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1, mt: 2, flexWrap: 'wrap' }}>
              <Button
                size="small"
                variant="outlined"
                disabled={pageWindowStart === 1}
                onClick={() => setPageWindowStart((start) => Math.max(1, start - pageWindowSize))}
              >
                {'<'}
              </Button>

              {Array.from({ length: Math.max(0, Math.min(totalPages, pageWindowStart + pageWindowSize - 1) - pageWindowStart + 1) }, (_, index) => pageWindowStart + index).map((page) => (
                <Button
                  key={page}
                  size="small"
                  variant={page === currentPage ? 'contained' : 'outlined'}
                  onClick={() => setCurrentPage(page)}
                >
                  {page}
                </Button>
              ))}

              <Button
                size="small"
                variant="outlined"
                disabled={Math.min(totalPages, pageWindowStart + pageWindowSize - 1) >= totalPages}
                onClick={() => setPageWindowStart((start) => Math.min(totalPages, start + pageWindowSize))}
              >
                {'>'}
              </Button>
            </Box>
          )}

          {/* Empty state */}
          {!loading && matchedCourses.length === 0 && (
            <Typography variant="body2" color="text.secondary" component="div" sx={{ mt: 2 }}>
              No courses found. Try a subject (e.g., "CS"), a code (e.g., "CS 171"), or a keyword (e.g., "chemistry").
            </Typography>
          )}
        </Box>
      </Box>

      {/* Mobile Drawer */}
      <Drawer anchor="left" open={sidebarOpen} onClose={() => setSidebarOpen(false)}>
        <Box sx={{ width: 300, p: 1 }}>
          <Typography variant="h6" sx={{ p: 2 }} component="div">My Channels</Typography>
          <Divider />
          <List>
            {myLoading ? (
              <MyChannelsSkeleton />
            ) : myCourses.length === 0 ? (
              <ListItem>
                <ListItemText
                  primary="No channels yet"
                  secondary="Join a course to pin it here"
                  primaryTypographyProps={{ component: 'div' }}
                  secondaryTypographyProps={{ component: 'div' }}
                />
              </ListItem>
            ) : (
              myCourses.map((c) => (
                <ListItemButton key={c.id} onClick={() => { handleCourseClick(c); setSidebarOpen(false); }}>
                  <ListItemIcon>
                    <Badge
                      color="error"
                      variant="dot"
                      overlap="circular"
                      invisible={!isCourseUnread(c.id)}
                      anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                    >
                      <Avatar sx={{ width: 32, height: 32, fontSize: 13, ...gradAvatarSx }}>{initials(c.code || c.name)}</Avatar>
                    </Badge>
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <Typography variant="body2" component="span" sx={{ fontWeight: 500 }}>{`${c.code} — ${c.name}`}</Typography>
                        {isCourseUnread(c.id) && <Chip size="small" color="error" label="New" />}
                      </Box>
                    }
                    secondary={`${(c as any).subject ?? c.department}${(c as any).number ? ` ${(c as any).number}` : ''}`}
                    primaryTypographyProps={{ component: 'div' }}
                    secondaryTypographyProps={{ noWrap: true, component: 'div' }}
                  />
                </ListItemButton>
              ))
            )}
          </List>
        </Box>
      </Drawer>

      {/* Course Channel Dialog */}
      <Dialog
        open={isDialogOpen}
        onClose={closeCourseDialog}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            height: { xs: '92vh', md: '92vh' },
            maxHeight: { xs: '92vh', md: '92vh' },
            overflow: 'hidden',
          },
        }}
      >
        {selectedCourse && (
          <>
            <DialogTitle sx={{ py: { xs: 1.25, sm: 2 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1.25, sm: 2 } }}>
                <Avatar sx={{ ...gradAvatarSx, width: { xs: 36, sm: 40 }, height: { xs: 36, sm: 40 } }}>
                  <School />
                </Avatar>
                <Box sx={{ minWidth: 0 }}>
                  {(() => {
                    const rawTitle = selectedCourse.name || '';
                    const cleaned = rawTitle.replace(/\s*\(?\d+(\.\d+)?(\s*-\s*\d+(\.\d+)?)?\s*Credits?(\s*Hours?)?\)?$/i, '');
                    return (
                      <>
                        <Typography
                          variant={isMobileDialog ? 'subtitle1' : 'h6'}
                          sx={{ lineHeight: 1.2, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
                        >
                          {selectedCourse.code} - {cleaned}
                        </Typography>
                        {(() => {
                          const hasCredits = typeof selectedCourse.credits === 'number' && !Number.isNaN(selectedCourse.credits);
                          if (!hasCredits) return (
                            <Typography variant="body2" color="text.secondary">{selectedCourse.department}</Typography>
                          );
                          const val = selectedCourse.credits as number;
                          const label = Number.isInteger(val) ? `${val.toFixed(0)} credits` : `${val} credits`;
                          return (
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                            >
                              {selectedCourse.department} • {label}
                            </Typography>
                          );
                        })()}
                      </>
                    );
                  })()}
                </Box>
                <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  {isMobileDialog && (
                    <IconButton
                      size="small"
                      aria-label="Channel info"
                      onClick={() => setMobileInfoOpen(true)}
                    >
                      <InfoOutlined fontSize="small" />
                    </IconButton>
                  )}
                  {selectedCourse?.joined && (
                    <IconButton
                      size="small"
                      aria-label="Channel actions"
                      aria-controls={isChannelMenuOpen ? 'channel-actions-menu' : undefined}
                      aria-haspopup="true"
                      aria-expanded={isChannelMenuOpen ? 'true' : undefined}
                      onClick={openChannelMenu}
                    >
                      <MoreVert fontSize="small" />
                    </IconButton>
                  )}
                  <IconButton
                    size="small"
                    aria-label="Close channel"
                    onClick={closeCourseDialog}
                  >
                    <Close fontSize="small" />
                  </IconButton>
                </Box>
              </Box>
            </DialogTitle>

            <Menu
              id="channel-actions-menu"
              anchorEl={channelMenuAnchorEl}
              open={isChannelMenuOpen}
              onClose={closeChannelMenu}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
              <MenuItem
                onClick={leaveChannelFromMenu}
                disabled={!selectedCourse?.joined || pendingCourseId === selectedCourse?.id}
                sx={{ color: 'error.main' }}
              >
                {pendingCourseId === selectedCourse?.id ? 'Leaving...' : 'Leave Channel'}
              </MenuItem>
            </Menu>
            
            <DialogContent sx={{ overflow: 'hidden' }}>
                          <Box
                            sx={{
                              display: 'grid',
                              gridTemplateColumns: { xs: '1fr', md: '320px 1fr' },
                              gap: 2,
                              height: '100%',
                            }}
                          >
                <Paper
                  variant="outlined"
                  sx={{
                                p: { xs: 1.5, sm: 2 },
                    borderRadius: 2,
                                height: { xs: 'auto', md: '100%' },
                    display: { xs: 'none', md: 'flex' },
                    flexDirection: 'column',
                    minHeight: 0,
                                maxHeight: { xs: 190, md: 'none' },
                                order: { md: 1 },
                  }}
                >
                              <Typography variant="subtitle2" color="text.secondary" component="div">{isMobileDialog ? 'About' : 'Channel Info'}</Typography>
                  <Box
                    sx={{
                      mt: 1,
                      flex: 1,
                      minHeight: 0,
                      overflowY: 'auto',
                      pr: 0.5,
                    }}
                  >
                    <Typography
                      variant="body2"
                      component="div"
                      sx={isMobileDialog ? { display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' } : undefined}
                    >
                      {selectedCourse.description || 'No description provided.'}
                    </Typography>
                  </Box>
                  <Divider sx={{ my: 1.5 }} />
                  <Typography variant="caption" color="text.secondary" component="div">Members</Typography>
                  <Typography variant="body2" component="div">{selectedCourse.memberCount ?? 0}</Typography>
                  <Box sx={{ mt: 1.5 }}>
                    <Button size="small" variant="outlined" fullWidth onClick={() => openMembersDialog(selectedCourse)}>View Members</Button>
                  </Box>
                </Paper>

                <Paper
                  variant="outlined"
                  sx={{
                    borderRadius: 2,
                    minHeight: 0,
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                    order: { md: 2 },
                  }}
                >
                  <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="subtitle2" component="div" sx={{ fontWeight: 700, color: 'primary.main' }}># {selectedCourse.code.toLowerCase()}</Typography>
                    <Typography variant="caption" color="text.secondary" component="div">{orderedPosts.length} messages</Typography>
                  </Box>

                  <Box ref={messagesContainerRef} sx={{ flex: 1, overflowY: 'auto', px: 2, py: 1.5 }}>
                    {(orderedPosts || []).map((post, idx) => {
                      const prev = orderedPosts[idx - 1];
                      const showIdentity = !prev || (((prev.author as any)?.id || prev.authorId) !== ((post.author as any)?.id || post.authorId));
                      return (
                      <React.Fragment key={post.id}>
                        <Box sx={{ mb: showIdentity ? 2 : 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25 }}>
                            {showIdentity ? (
                              <Avatar
                                src={(post.author as any)?.profileImage || undefined}
                                sx={{ width: 34, height: 34, fontSize: 14, ...gradAvatarSx, cursor: ((post.author as any)?.id || post.authorId) ? 'pointer' : 'default' }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  goToProfile((post.author as any)?.id || post.authorId);
                                }}
                              >
                                {initials(getAuthorLabel(post.author, post.authorId))}
                              </Avatar>
                            ) : (
                              <Box sx={{ width: 34 }} />
                            )}
                            <Box sx={{ minWidth: 0, flex: 1 }}>
                              {showIdentity && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0.35 }}>
                                  <Typography
                                    variant="subtitle2"
                                    component="div"
                                    sx={{ fontWeight: 700, color: 'primary.main', cursor: ((post.author as any)?.id || post.authorId) ? 'pointer' : 'default' }}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      goToProfile((post.author as any)?.id || post.authorId);
                                    }}
                                  >
                                    {getAuthorLabel(post.author, post.authorId)}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary" component="div">{formatTimeAgo(post.createdAt)}</Typography>
                                </Box>
                              )}
                              <Box sx={{ display: 'inline-flex', alignItems: 'flex-end', gap: 0.6, maxWidth: { xs: '96%', md: '88%' } }}>
                                <Paper
                                  elevation={0}
                                  sx={{
                                    px: 1.25,
                                    py: 0.85,
                                    borderRadius: 2.5,
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    backgroundColor: (theme) => (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : theme.palette.background.paper),
                                  }}
                                >
                                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }} component="div">
                                    {post.content}
                                  </Typography>
                                </Paper>
                                {!showIdentity && formatMessageTime(post.createdAt) && (
                                  <Typography variant="caption" color="text.secondary" sx={{ minWidth: '2.2rem', textAlign: 'center', fontWeight: 600 }} component="div">
                                    {formatMessageTime(post.createdAt)}
                                  </Typography>
                                )}
                              </Box>
                            <Box sx={{ mt: 0.5, display: 'flex', alignItems: 'center', gap: 1.25 }}>
                              <Button
                                size="small"
                                variant="text"
                                sx={{ minWidth: 0, px: 0 }}
                                onClick={() => setReplyTarget({
                                  postId: post.id,
                                  label: `Replying to ${getAuthorLabel(post.author, post.authorId)}`,
                                })}
                              >
                                Reply
                              </Button>
                              {me?.id && ((((post.author as any)?.id === me.id) || post.authorId === me.id || isSuperAdmin)) && (
                                <Button
                                  size="small"
                                  variant="text"
                                  color="error"
                                  sx={{ minWidth: 0, px: 0 }}
                                  disabled={deletingPostId === post.id}
                                  onClick={() => setConfirmRemoveTarget({ postId: post.id })}
                                >
                                  {deletingPostId === post.id ? 'Deleting...' : 'Delete'}
                                </Button>
                              )}
                            </Box>

                            {(post.comments || []).map((comment) => (
                              <Box key={comment.id} sx={{ mt: 1.25, pl: 1.25, borderLeft: '2px solid', borderColor: 'divider' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                                  <Typography variant="caption" fontWeight={700} component="div">{getAuthorLabel(comment.author, comment.author?.id)}</Typography>
                                  <Typography variant="caption" color="text.secondary" component="div">{formatTimeAgo(comment.createdAt)}</Typography>
                                </Box>
                                <Typography variant="body2" sx={{ mt: 0.25 }} component="div">{comment.content || '[deleted]'}</Typography>
                                <Button
                                  size="small"
                                  variant="text"
                                  sx={{ minWidth: 0, px: 0, mt: 0.25 }}
                                  onClick={() => setReplyTarget({
                                    postId: post.id,
                                    parentId: comment.id,
                                    label: `Replying to ${getAuthorLabel(comment.author, comment.author?.id)}`,
                                  })}
                                >
                                  Reply
                                </Button>
                                {me?.id && (comment.author?.id === me.id || isSuperAdmin) && (
                                  <Button
                                    size="small"
                                    variant="text"
                                    color="error"
                                    sx={{ minWidth: 0, px: 0, mt: 0.25, ml: 1 }}
                                    disabled={deletingCommentId === comment.id}
                                    onClick={() => setConfirmRemoveTarget({ postId: post.id, commentId: comment.id })}
                                  >
                                    {deletingCommentId === comment.id ? 'Removing...' : 'Remove'}
                                  </Button>
                                )}

                                {(comment.children || []).map((child) => (
                                  <Box key={child.id} sx={{ mt: 1, pl: 1.25, borderLeft: '2px dashed', borderColor: 'divider' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                                      <Typography variant="caption" fontWeight={700} component="div">{getAuthorLabel(child.author, child.author?.id)}</Typography>
                                      <Typography variant="caption" color="text.secondary" component="div">{formatTimeAgo(child.createdAt)}</Typography>
                                    </Box>
                                    <Typography variant="body2" sx={{ mt: 0.25 }} component="div">{child.content || '[deleted]'}</Typography>
                                    {me?.id && (child.author?.id === me.id || isSuperAdmin) && (
                                      <Button
                                        size="small"
                                        variant="text"
                                        color="error"
                                        sx={{ minWidth: 0, px: 0, mt: 0.25 }}
                                        disabled={deletingCommentId === child.id}
                                        onClick={() => setConfirmRemoveTarget({ postId: post.id, commentId: child.id })}
                                      >
                                        {deletingCommentId === child.id ? 'Removing...' : 'Remove'}
                                      </Button>
                                    )}
                                  </Box>
                                ))}
                              </Box>
                            ))}
                          </Box>
                        </Box>
                        </Box>
                        {idx < orderedPosts.length - 1 && !isSameDay(post.createdAt, orderedPosts[idx + 1].createdAt) && (
                          <Box sx={{ display: 'flex', justifyContent: 'center', my: 0.75 }}>
                            <Typography
                              variant="caption"
                              sx={{
                                px: 1.1,
                                py: 0.2,
                                borderRadius: 99,
                                border: '1px solid',
                                borderColor: 'divider',
                                backgroundColor: (theme) => (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.10)' : theme.palette.background.paper),
                                color: 'text.secondary',
                                fontWeight: 700,
                              }}
                            >
                              {formatDaySeparator(orderedPosts[idx + 1].createdAt)}
                            </Typography>
                          </Box>
                        )}
                      </React.Fragment>
                      );
                    })}

                    {!postsLoading && postsCursor && (
                      <Box sx={{ textAlign: 'center', pb: 1 }}>
                        <Button onClick={async () => {
                          if (!selectedCourse || !postsCursor) return;
                          try {
                            const resp: any = await apiListCoursePosts(selectedCourse.id, postsCursor);
                            setPosts(prev => [...prev, ...((resp.items || []).map(shapeFromDto))]);
                            setPostsCursor(resp.nextCursor || null);
                          } catch (e) {
                            console.error('load more posts failed', e);
                          }
                        }}>Load more</Button>
                      </Box>
                    )}
                  </Box>

                  <Box sx={{ borderTop: '1px solid', borderColor: 'divider', p: 1.5 }}>
                    {replyTarget && (
                      <Box sx={{ mb: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                        <Typography variant="caption" color="text.secondary" component="div">{replyTarget.label}</Typography>
                        <Button size="small" variant="text" onClick={() => setReplyTarget(null)}>Cancel</Button>
                      </Box>
                    )}
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <TextField
                        fullWidth
                        size="small"
                        inputRef={composerInputRef}
                        disabled={!selectedCourse.joined}
                        placeholder={
                          !selectedCourse.joined
                            ? 'Join this channel to send messages'
                            : replyTarget
                              ? 'Write a reply…'
                              : 'Message this channel…'
                        }
                        value={newPostContent}
                        onChange={(e) => setNewPostContent(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();
                            if (selectedCourse.joined && newPostContent.trim()) handleCreatePost();
                          }
                        }}
                      />
                      <Button
                        variant="contained"
                        onClick={handleCreatePost}
                        disabled={!selectedCourse.joined || !newPostContent.trim()}
                      >
                        Send
                      </Button>
                    </Box>
                  </Box>
                </Paper>
              </Box>
            </DialogContent>
            
            {!selectedCourse.joined && (
              <DialogActions sx={{ px: { xs: 1.5, sm: 2 }, py: 1.25 }}>
                <Box sx={{ flexGrow: 1 }} />
                <Button variant="contained" onClick={() => handleJoin(selectedCourse)}>Join Channel</Button>
              </DialogActions>
            )}
          </>
        )}
      </Dialog>

      <Dialog open={mobileInfoOpen} onClose={() => setMobileInfoOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Channel Info</DialogTitle>
        <DialogContent dividers>
          {selectedCourse && (
            <>
              <Typography variant="subtitle2" color="text.secondary" component="div">About</Typography>
              <Typography variant="body2" sx={{ mt: 1 }} component="div">
                {selectedCourse.description || 'No description provided.'}
              </Typography>
              <Divider sx={{ my: 1.5 }} />
              <Typography variant="caption" color="text.secondary" component="div">Members</Typography>
              <Typography variant="body2" component="div">{selectedCourse.memberCount ?? 0}</Typography>
              <Box sx={{ mt: 1.5 }}>
                <Button
                  size="small"
                  variant="outlined"
                  fullWidth
                  onClick={() => {
                    setMobileInfoOpen(false);
                    openMembersDialog(selectedCourse);
                  }}
                >
                  View Members
                </Button>
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMobileInfoOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Members Dialog */}
      <Dialog open={membersOpen} onClose={() => setMembersOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Channel Members</DialogTitle>
        <DialogContent dividers>
          <TextField fullWidth placeholder="Search members..." value={membersQuery} onChange={e => setMembersQuery(e.target.value)} size="small" InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }} sx={{ mb: 2 }} />
          <List>
            {members.map(m => (
              <ListItemButton key={m.id} onClick={() => goToProfile(m.id)} sx={{ borderRadius: 1 }}>
                <ListItemAvatar>
                  <Avatar src={(m as any).avatarUrl || undefined} sx={gradAvatarSx}>{(m.displayName || `${m.firstName||''} ${m.lastName||''}`).trim().slice(0,2).toUpperCase()}</Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={<Typography variant="subtitle2" component="div">{m.displayName || `${m.firstName||''} ${m.lastName||''}`}</Typography>}
                  secondary={<Typography variant="caption" component="div">{[m.major, m.year ? `Year ${m.year}`: null].filter(Boolean).join(' • ')}</Typography>}
                />
              </ListItemButton>
            ))}
            {!membersLoading && members.length === 0 && <ListItem><ListItemText primary="No members found" /></ListItem>}
          </List>
          {membersCursor && !membersLoading && (
            <Box sx={{ textAlign: 'center', mt: 1 }}>
              <Button size="small" onClick={async () => {
                if (!selectedCourse || !membersCursor) return;
                try {
                  const r = await apiListCourseMembers(selectedCourse.id, membersQDeb || undefined, membersCursor, 25);
                  setMembers(prev => [...prev, ...(r.items || [])]);
                  setMembersCursor(r.nextCursor || null);
                } catch (e) { console.error('load more members failed', e); }
              }}>Load more</Button>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMembersOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!confirmRemoveTarget}
        onClose={() => {
          if (!deletingPostId && !deletingCommentId) setConfirmRemoveTarget(null);
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Confirm Removal</DialogTitle>
        <DialogContent dividers>
          <Typography>Are you sure you want to remove this message?</Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setConfirmRemoveTarget(null)}
            disabled={!!deletingPostId || !!deletingCommentId}
          >
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            disabled={!!deletingPostId || !!deletingCommentId}
            onClick={() => {
              if (!confirmRemoveTarget) return;
              if (confirmRemoveTarget.commentId) {
                void handleDeleteComment(confirmRemoveTarget.postId, confirmRemoveTarget.commentId);
                return;
              }
              void handleDeletePost(confirmRemoveTarget.postId);
            }}
          >
            {deletingPostId || deletingCommentId ? 'Removing...' : 'Yes, remove'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CourseChannels;